"use client";

import { clearAuthTokens, isLoggedIn, setAuthTokens } from "axios-jwt";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { AuthorizeRequest, LoginValidateResponse } from "./index";

export interface OtpAuthNotify {
  info: (message: string) => void;
  error: (message: string) => void;
}

export interface CreateOtpAuthProviderOptions<
  TUser,
  TExtra = Record<string, never>
> {
  signIn: (args: { email: string }) => Promise<void>;
  signOut: () => Promise<void>;
  authorize: (input: AuthorizeRequest) => Promise<LoginValidateResponse<TUser>>;
  isPublicPath: (pathname: string | null) => boolean;
  /** Substring match on pathname for the OTP verification step */
  verifyPathIncludes: string;
  buildVerifyUrl: (email: string) => string;
  homePath: string;
  signInRedirectPath: string;
  notify: OtpAuthNotify;
  messages: {
    sessionExpired?: string;
    loginCodeSent?: string;
    emailNotFound?: string;
    welcome?: string;
    welcomeWithFirstName?: (firstName: string) => string;
    verifyMissingEmail?: string;
    logoutFailed?: string;
  };
  /** When true, session-expired copy uses `notify.info` before redirect */
  notifySessionExpired?: boolean;
  syncEmailFromSearchParams?: boolean;
  /** Charity-style: on verify route without email in context, redirect to sign-in */
  redirectVerifyWithoutEmail?: boolean;
  /** After OTP success, run checkAuth without the “verify without email” redirect branch */
  authorizeSkipsVerifyEmailGuard?: boolean;
  useExtra?: (authenticated: boolean) => TExtra;
  autoAuthorizeSearchParams?: boolean;
  /** When false, authorize errors only `console.error` (admin behavior) */
  notifyOnAuthorizeError?: boolean;
  /** When false, login request errors only `console.error` (admin behavior) */
  notifyOnLoginError?: boolean;
  /** When true, logout failures toast the exception message (donor behavior) */
  notifyLogoutExceptionMessage?: boolean;
  /**
   * When true, non-public routes render `protectedRouteFallback` until the first
   * `isLoggedIn` check finishes and `authenticated` is true (or redirect runs).
   */
  gateProtectedRoutes?: boolean;
  /** UI while protected routes are gated (e.g. full-screen loader). */
  protectedRouteFallback?: ReactNode;
}

type BaseOtpAuthContext = {
  authenticated: boolean;
  loginRequest: (email: string) => Promise<void>;
  authorizeRequest: (code: string, email?: string) => Promise<void>;
  logout: () => Promise<void>;
  email: string | undefined;
  setEmail: (e: string) => void;
};

export function createOtpAuthProvider<TUser, TExtra = Record<string, never>>(
  options: CreateOtpAuthProviderOptions<TUser, TExtra>
) {
  type Ctx = BaseOtpAuthContext & TExtra;
  const AuthContext = createContext<Ctx | undefined>(undefined);

  const AuthProvider = ({ children }: { children: ReactNode }) => {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const loginRef = useRef(false);
    const optsRef = useRef(options);
    optsRef.current = options;

    const [authenticated, setAuthenticated] = useState(false);
    const [authCheckSettled, setAuthCheckSettled] = useState(false);
    const [email, setEmail] = useState<string | undefined>();

    const extra = optsRef.current.useExtra?.(authenticated) ?? ({} as TExtra);

    const checkAuth = useCallback(
      async (noRedirectForVerifyEmail?: boolean) => {
        try {
          const o = optsRef.current;
          const loggedIn = await isLoggedIn();
          setAuthenticated(loggedIn);

          if (!loggedIn && !o.isPublicPath(pathname)) {
            if (o.notifySessionExpired !== false && o.messages.sessionExpired) {
              o.notify.info(o.messages.sessionExpired);
            }
            router.push(o.signInRedirectPath);
          }

          if (
            o.redirectVerifyWithoutEmail &&
            pathname?.includes(o.verifyPathIncludes) &&
            !email &&
            !noRedirectForVerifyEmail
          ) {
            if (o.messages.verifyMissingEmail) {
              o.notify.info(o.messages.verifyMissingEmail);
            }
            router.push(o.signInRedirectPath);
          }
        } finally {
          setAuthCheckSettled(true);
        }
      },
      [email, pathname, router]
    );

    const sendingOtpRef = useRef(false);
    const loginRequest = useCallback(
      async (em: string) => {
        if (sendingOtpRef.current) return;
        sendingOtpRef.current = true;
        const o = optsRef.current;
        try {
          await o.signIn({ email: em });
          setEmail(em);
          if (o.messages.loginCodeSent) {
            o.notify.info(o.messages.loginCodeSent);
          }
          router.push(o.buildVerifyUrl(em));
        } catch (e: unknown) {
          sendingOtpRef.current = false;
          console.error(e);
          if (o.notifyOnLoginError !== false) {
            const msg = e instanceof Error ? e.message : "Error";
            o.notify.error(msg);
          }
        }
      },
      [router]
    );

    const authorizeRequest = useCallback(
      async (code: string, inputEmail?: string) => {
        const o = optsRef.current;
        try {
          const userEmail = inputEmail ?? email;
          if (!userEmail) {
            o.notify.error(o.messages.emailNotFound ?? "No email");
            router.push(o.signInRedirectPath);
            return;
          }
          const response = await o.authorize({
            mode: "otp",
            input: { email: userEmail, otp: code },
          });
          await setAuthTokens({
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
          } as unknown as Parameters<typeof setAuthTokens>[0]);
          await checkAuth(o.authorizeSkipsVerifyEmailGuard);
          router.push(o.homePath);
          if (response.firstName && o.messages.welcomeWithFirstName) {
            o.notify.info(o.messages.welcomeWithFirstName(response.firstName));
          } else if (o.messages.welcome) {
            o.notify.info(o.messages.welcome);
          }
        } catch (e: unknown) {
          console.error(e);
          const opts = optsRef.current;
          if (opts.notifyOnAuthorizeError !== false) {
            const msg = e instanceof Error ? e.message : "Error";
            opts.notify.error(msg);
          }
        }
      },
      [checkAuth, email, router]
    );

    useEffect(() => {
      const o = optsRef.current;
      if (!o.syncEmailFromSearchParams && !o.autoAuthorizeSearchParams) {
        return;
      }
      const searchEmail = searchParams?.get("email");
      const code = searchParams?.get("code");
      if (o.syncEmailFromSearchParams && !email && searchEmail) {
        setEmail(searchEmail);
      }
      if (
        o.autoAuthorizeSearchParams &&
        searchEmail &&
        code &&
        !loginRef.current
      ) {
        loginRef.current = true;
        void authorizeRequest(code, searchEmail);
      }
    }, [authorizeRequest, email, searchParams]);

    const logout = useCallback(async () => {
      const o = optsRef.current;
      try {
        await o.signOut();
      } catch (e: unknown) {
        console.error(e);
        if (o.messages.logoutFailed) {
          o.notify.error(o.messages.logoutFailed);
        } else if (o.notifyLogoutExceptionMessage) {
          o.notify.error(e instanceof Error ? e.message : "Error");
        }
      }
      await clearAuthTokens();
      await checkAuth();
    }, [checkAuth]);

    useEffect(() => {
      void checkAuth();
      const onVisible = () => {
        if (document.visibilityState === "visible") {
          void checkAuth();
        }
      };
      const onFocus = () => void checkAuth();
      document.addEventListener("visibilitychange", onVisible);
      window.addEventListener("focus", onFocus);
      return () => {
        document.removeEventListener("visibilitychange", onVisible);
        window.removeEventListener("focus", onFocus);
      };
    }, [checkAuth]);

    const base: BaseOtpAuthContext = useMemo(
      () => ({
        authenticated,
        loginRequest,
        authorizeRequest,
        logout,
        email,
        setEmail,
      }),
      [authenticated, authorizeRequest, email, loginRequest, logout]
    );

    const context = { ...base, ...extra } as Ctx;

    const o = optsRef.current;
    const onPublicRoute = o.isPublicPath(pathname);
    const gateProtected =
      o.gateProtectedRoutes &&
      !onPublicRoute &&
      (!authCheckSettled || !authenticated);

    return (
      <AuthContext.Provider value={context}>
        {gateProtected ? (o.protectedRouteFallback ?? null) : children}
      </AuthContext.Provider>
    );
  };

  function useAuth(): Ctx {
    const ctx = useContext(AuthContext);
    if (ctx === undefined) {
      throw new Error("useAuth must be used within an AuthProvider");
    }
    return ctx;
  }

  return { AuthProvider, useAuth };
}
